import type { Node, Workflow } from '@/stores/workflowStore';
import { getNodeLabel } from '@/lib/workflowUtils';
import { inferSemanticRole } from '@/lib/blockContext';

export interface BlockAutoConfigureResult {
  workflow: Workflow;
  changes: string[];
}

export interface SmartBlockInputRequirement {
  key: string;
  label: string;
  reason: string;
  inputKind: 'text' | 'url' | 'textarea';
  placeholder?: string;
  suggestedValue?: string;
}

export interface SmartBlockReadinessContractField {
  key: string;
  label: string;
  inputKind: 'text' | 'url' | 'textarea';
  placeholder?: string;
  requiredWhen: (node: Node) => boolean;
  isReady: (value: unknown) => boolean;
  humanOwned: boolean;
  reason: string;
}

export interface SmartBlockReadinessContract {
  nodeType: Node['type'];
  nodeSubtype?: string;
  fields: SmartBlockReadinessContractField[];
}

const inferredIoByRole: Record<string, 'call' | 'receive' | 'send' | undefined> = {
  call: 'call',
  receive: 'receive',
  send: 'send',
  agent: 'call',
  drone: 'send',
};

const smartReadinessContracts: SmartBlockReadinessContract[] = [
  {
    nodeType: 'tool',
    nodeSubtype: 'http',
    fields: [
      {
        key: 'url',
        label: 'HTTP URL',
        inputKind: 'url',
        placeholder: 'https://api.your-service.com/path',
        requiredWhen: () => true,
        isReady: (v) => {
          const s = String(v ?? '').trim();
          return Boolean(s) && !/example\.com/i.test(s);
        },
        humanOwned: true,
        reason: 'Smart Blocks needs the real endpoint for this HTTP step.',
      },
    ],
  },
  {
    nodeType: 'trigger',
    nodeSubtype: 'webhook',
    fields: [
      {
        key: 'url',
        label: 'Webhook trigger URL',
        inputKind: 'url',
        placeholder: 'https://your-domain.com/inbound/webhook',
        requiredWhen: () => true,
        isReady: (v) => {
          const s = String(v ?? '').trim();
          return Boolean(s) && !/example\.com/i.test(s);
        },
        humanOwned: true,
        reason: 'Provide the inbound webhook endpoint this trigger should listen to.',
      },
    ],
  },
  {
    nodeType: 'action',
    nodeSubtype: 'webhook',
    fields: [
      {
        key: 'url',
        label: 'Webhook destination URL',
        inputKind: 'url',
        placeholder: 'https://your-domain.com/outbound/webhook',
        requiredWhen: () => true,
        isReady: (v) => {
          const s = String(v ?? '').trim();
          return Boolean(s) && !/example\.com/i.test(s);
        },
        humanOwned: true,
        reason: 'Provide the outbound webhook endpoint this action should call.',
      },
    ],
  },
  {
    nodeType: 'tool',
    nodeSubtype: 'transform',
    fields: [
      {
        key: 'transform',
        label: 'Transform logic',
        inputKind: 'textarea',
        placeholder: 'return { ...input, normalized: true };',
        requiredWhen: () => true,
        isReady: (v) => {
          const s = String(v ?? '').trim();
          return Boolean(s) && !/^return input;?$/i.test(s);
        },
        humanOwned: true,
        reason: 'Smart Blocks needs your custom mapping logic for this transform step.',
      },
    ],
  },
  {
    nodeType: 'goal',
    nodeSubtype: 'outcome',
    fields: [
      {
        key: 'outcome',
        label: 'Goal outcome',
        inputKind: 'textarea',
        placeholder: 'Create a weekly applicant follow-up digest every Friday',
        requiredWhen: () => true,
        isReady: (v) => {
          const s = String(v ?? '').trim();
          return Boolean(s) && !/^accomplish:/i.test(s);
        },
        humanOwned: true,
        reason: 'Define the exact human outcome this mission should accomplish.',
      },
    ],
  },
  {
    nodeType: 'agent',
    fields: [
      {
        key: 'agentBrief',
        label: 'Agent brief',
        inputKind: 'textarea',
        placeholder: 'Prioritize urgent leads, then schedule follow-ups and summarize risks.',
        requiredWhen: () => true,
        isReady: (v) => {
          const s = String(v ?? '').trim();
          return Boolean(s) && !/^coordinate mission decisions/i.test(s);
        },
        humanOwned: true,
        reason: 'Provide the specific decision policy for this agent block.',
      },
    ],
  },
  {
    nodeType: 'drone',
    fields: [
      {
        key: 'droneTask',
        label: 'Drone task',
        inputKind: 'textarea',
        placeholder: 'Run nightly sync against CRM and upload changed records.',
        requiredWhen: () => true,
        isReady: (v) => {
          const s = String(v ?? '').trim();
          return Boolean(s) && !/^execute remote mission step/i.test(s);
        },
        humanOwned: true,
        reason: 'Provide the exact remote task this drone should run.',
      },
    ],
  },
];

export function getSmartBlockReadinessContract(node: Node): SmartBlockReadinessContract | null {
  return (
    smartReadinessContracts.find(
      (c) => c.nodeType === node.type && (c.nodeSubtype ? c.nodeSubtype === node.subtype : true)
    ) ?? null
  );
}

function inferredIoDirection(node: Node): 'call' | 'receive' | 'send' {
  const role = inferSemanticRole(node.type, String(node.subtype));
  return (
    inferredIoByRole[role] ??
    (node.type === 'trigger' ? 'receive' : node.type === 'action' ? 'send' : 'call')
  );
}

function goalIntent(workflow: Workflow): string {
  const goal = workflow.nodes.find((n) => n.type === 'goal' && n.subtype === 'outcome');
  const outcome = String(goal?.config.outcome ?? '').trim();
  return outcome || workflow.name || 'mission outcome';
}

function derivePurpose(node: Node, prev: Node | null, next: Node | null, intent: string): string {
  const prevLabel = prev ? getNodeLabel(prev) : 'the previous step';
  const nextLabel = next ? getNodeLabel(next) : 'the next step';

  if (node.type === 'trigger') return `Receive input for "${intent}" and begin the flow.`;
  if (node.type === 'tool' && node.subtype === 'http') {
    return `Call external service after ${prevLabel} and pass results toward ${nextLabel}.`;
  }
  if (node.type === 'tool' && node.subtype === 'transform') {
    return `Transform data from ${prevLabel} into the shape needed for ${nextLabel}.`;
  }
  if (node.type === 'tool' && node.subtype === 'conditional') {
    return `Route execution based on conditions before continuing to ${nextLabel}.`;
  }
  if (node.type === 'action' && node.subtype === 'notification') {
    return `Send a user-facing update that "${intent}" is progressing.`;
  }
  if (node.type === 'action' && node.subtype === 'log') {
    return `Record trace output for this mission around ${prevLabel}.`;
  }
  if (node.type === 'action' && node.subtype === 'webhook') {
    return `Deliver mission output to downstream system for "${intent}".`;
  }
  if (node.type === 'agent') return `Reason over ${prevLabel} and decide the next mission action toward "${intent}".`;
  if (node.type === 'drone') return `Execute remote work from ${prevLabel} and report status to ${nextLabel}.`;
  if (node.type === 'goal') return `Define and anchor the mission outcome: "${intent}".`;
  if (node.type === 'start') return `Mission entrypoint for "${intent}".`;
  return `Carry mission flow from ${prevLabel} to ${nextLabel}.`;
}

function applySubtypeDefaults(node: Node, workflow: Workflow): Node {
  const config = { ...node.config };
  const intent = goalIntent(workflow);

  if (node.type === 'trigger' && node.subtype === 'manual' && !String(config.name ?? '').trim()) {
    config.name = `Start ${workflow.name}`;
  }
  if (node.type === 'tool' && node.subtype === 'http') {
    if (!String(config.url ?? '').trim()) config.url = 'https://example.com/api';
    if (!String(config.method ?? '').trim()) config.method = 'POST';
  }
  if (node.type === 'tool' && node.subtype === 'transform' && !String(config.transform ?? '').trim()) {
    config.transform = 'return input;';
  }
  if (node.type === 'action' && node.subtype === 'notification' && !String(config.message ?? '').trim()) {
    config.message = `Update: ${intent}`;
  }
  if (node.type === 'action' && node.subtype === 'log' && !String(config.message ?? '').trim()) {
    config.message = `${workflow.name} step completed`;
  }
  if (node.type === 'action' && node.subtype === 'webhook' && !String(config.url ?? '').trim()) {
    config.url = 'https://example.com/webhook';
  }
  if (node.type === 'goal' && node.subtype === 'outcome' && !String(config.outcome ?? '').trim()) {
    config.outcome = `Accomplish: ${workflow.name}`;
  }
  if (node.type === 'agent' && !String(config.agentBrief ?? '').trim()) {
    config.agentBrief = `Coordinate mission decisions to achieve "${intent}".`;
  }
  if (node.type === 'drone' && !String(config.droneTask ?? '').trim()) {
    config.droneTask = `Execute remote mission step for "${intent}".`;
  }

  return { ...node, config };
}

export function autoConfigureWorkflowNodes(
  workflowInput: Workflow,
  nodeIds?: string[]
): BlockAutoConfigureResult {
  const workflow: Workflow = {
    ...workflowInput,
    nodes: structuredClone(workflowInput.nodes),
    edges: structuredClone(workflowInput.edges),
    updatedAt: new Date(),
  };
  const scope = nodeIds?.length ? new Set(nodeIds) : null;
  const incoming = new Map<string, string[]>();
  const outgoing = new Map<string, string[]>();
  workflow.nodes.forEach((n) => {
    incoming.set(n.id, []);
    outgoing.set(n.id, []);
  });
  workflow.edges.forEach((e) => {
    incoming.get(e.to)?.push(e.from);
    outgoing.get(e.from)?.push(e.to);
  });

  const changes: string[] = [];
  const intent = goalIntent(workflow);

  workflow.nodes = workflow.nodes.map((node) => {
    if (scope && !scope.has(node.id)) return node;
    const prevId = incoming.get(node.id)?.[0] ?? null;
    const nextId = outgoing.get(node.id)?.[0] ?? null;
    const prevNode = prevId ? workflow.nodes.find((n) => n.id === prevId) ?? null : null;
    const nextNode = nextId ? workflow.nodes.find((n) => n.id === nextId) ?? null : null;

    let updated = applySubtypeDefaults(node, workflow);
    const cfg = { ...updated.config };

    if (!String(cfg.contextLabel ?? '').trim()) {
      cfg.contextLabel = getNodeLabel(updated);
    }
    if (!String(cfg.contextPurpose ?? '').trim()) {
      cfg.contextPurpose = derivePurpose(updated, prevNode, nextNode, intent);
    }
    if (!String(cfg.semanticRole ?? '').trim()) {
      cfg.semanticRole = inferSemanticRole(updated.type, String(updated.subtype));
    }
    if (!String(cfg.ioDirection ?? '').trim()) {
      cfg.ioDirection = inferredIoDirection(updated);
    }
    cfg.placementStatus =
      (incoming.get(node.id)?.length ?? 0) > 0 || (outgoing.get(node.id)?.length ?? 0) > 0
        ? 'connected'
        : 'unconfigured';
    if (prevNode) cfg.connectsFrom = getNodeLabel(prevNode);
    if (nextNode) cfg.connectsTo = getNodeLabel(nextNode);

    const changed = JSON.stringify(node.config) !== JSON.stringify(cfg);
    if (changed) changes.push(`Configured ${getNodeLabel(updated)}`);
    updated = { ...updated, config: cfg };
    return updated;
  });

  return { workflow, changes };
}

export function getSmartBlockInputRequirements(node: Node): SmartBlockInputRequirement[] {
  const contract = getSmartBlockReadinessContract(node);
  if (!contract) return [];
  return contract.fields
    .filter((field) => field.requiredWhen(node) && !field.isReady(node.config[field.key]))
    .map((field) => ({
      key: field.key,
      label: field.label,
      reason: field.reason,
      inputKind: field.inputKind,
      placeholder: field.placeholder,
    }));
}
